<!-- Introduction -->
<!-- CTA - Introducing the Screen for EPI in CP tool for clinicians -->
<div class="cta cta--introducing">
    <div class="c-img">
        <picture class="cta__image">
            <source media="(max-width: 360px)" srcset="assets/images/ctas/introducing-the-screen-for-epi-in-cp-tool-mobile.jpg">
            <source media="(max-width: 719px)" srcset="assets/images/ctas/introducing-the-screen-for-epi-in-cp-tool-mobile@2x.jpg">
            <source media="(max-width: 1440px)" srcset="assets/images/ctas/introducing-the-screen-for-epi-in-cp-tool-full-height.png">
            <source media="(max-width: 2880px)" srcset="assets/images/ctas/introducing-the-screen-for-epi-in-cp-tool-full-height@2x.png">
            <img src="assets/images/ctas/introducing-the-screen-for-epi-in-cp-tool-full-height@3x.png" alt="About this tool image">
        </picture>
    </div>
    <div class="cta__info">
        <h2 class="cta__title ">Introducing the Screen for EPI in CP tool for clinicians</h2>
        <p class="cta__text">The Screen for EPI in CP tool has been developed with the help of leading experts to provide US healthcare professionals with a tool to screen patients with definite chronic pancreatitis (CP) who may have exocrine pancreatic insufficiency (EPI).</p>
        <p class="cta__text">This screening tool uses a Classification and Regression Tree (CART) decision tree model based on commonly available patient information to predict whether patients are likely to have EPI or not.</p>
        <app-button path="screening-tool-landing" tab="start-screening" text="Start screening" (btnClick)="track('screening-tool-landing-introduction-start-screening-top-cta')"></app-button>
    </div>
</div>
<!-- CTA - How to use -->
<div class="cta cta--how-to-use">
    <div class="cta__info">
        <h2 class="cta__title">How to use this tool</h2>
        <h3 class="cta__subtitle">Designed to support US healthcare professionals</h3>
        <p class="cta__text"><b>The Screen For EPI in CP tool</b> uses a series of simple yes/no/unknown answers to questions about certain demographics, laboratory values, habits, medication, stool symptom, and pancreatic imaging data from your patient as input. You will move through the decision tree based on the answers to 2–7 questions and will be provided with a final assessment: either <i>“Your patient is more likely to have EPI”</i> or <i>“Your patient is more likely to not have EPI.”</i> You will also be provided with a summary of the pathway taken, a risk score (ie, the percentage risk for your patient having EPI or for not having EPI, based on the cohort data in which the tool was developed), and references to support the result.</p>
        <!-- Dropdown -->
        <div class="dropdown dropdown--introduction introduction-dropdown">
            <div class="dropdown__toggle" [ngClass]="{'dropdown__toggle--active' : dropdownIsActive}" (click)="toggleDropdown()">
                <p class="dropdown__toggle-text">View variables included in the Screen for EPI in CP tool</p>
                <img *ngIf="!dropdownIsActive" src="assets/icons/icon-dropdown-arrow-black.svg" alt="Dropdown arrow">
                <img *ngIf="dropdownIsActive" src="assets/icons/icon-dropdown-arrow-white.svg" alt="Dropdown arrow">
            </div>
            <div *ngIf="dropdownIsActive" class="dropdown__list">
                <div class="list">
                    <div class="list__column">
                        <p class="list__title">With imaging</p>
                        <ol>
                            <li class="list__item">Steatorrhea* (yes/no)</li>
                            <li class="list__item">Smoking quantity (packs of cigarettes per year, n)</li>
                            <li class="list__item">Pancreatic atrophy (yes/no)</li>
                            <li class="list__item">Pancreatic duct contour abnormality (yes/no)</li>
                            <li class="list__item">Serum hemoglobin* (g/dL)</li>
                            <li class="list__item">Serum 25-OH vitamin D*, **, *** (ng/mL)</li>
                            <li class="list__item">Current alcohol use (yes/no)</li>
                            <li class="list__item">Pancreatic parenchymal calcification (yes/no)</li>
                            <li class="list__item">Use of opiate analgesics (yes/no)</li>
                            <li class="list__item">Serum albumin* (g/dL)</li>
                        </ol>
                    </div>
                    <div class="list__column">
                        <p class="list__title">Without imaging</p>
                        <ol>
                            <li class="list__item">Steatorrhea* (yes/no)</li>
                            <li class="list__item">Smoking quantity (packs of cigarettes per year, n)</li>
                            <li class="list__item">Current alcohol use (yes/no)</li>
                            <li class="list__item">Serum lipase (U/L)</li>
                            <li class="list__item">Serum hemoglobin* (g/dL)</li>
                            <li class="list__item">Serum hemoglobin A1c* (%)</li>
                            <li class="list__item">Age (in years)</li>
                            <li class="list__item">Body weight (lbs)</li>
                        </ol>
                    </div>
                </div>
                <hr>
                <div class="horizontal-block"><img src="assets/icons/icon-print.svg" class="print-icon" alt="Print icon"><p><a href="assets/pdfs/Variables-01.pdf" target="_blank" (click)="track('screening-tool-landing-introduction-print-variables')">Print variables</a></p></div>
                <div class="dropdown__disclaimer">
                    <p class="dropdown__disclaimer-copy">Depending on the individual patient, only between 2 and 6 of the variables shown are needed. <a (click)="openTable()">View the full list of variables</a>, *Must be in absence of pancreatic enzyme replacement therapy; **must be in absence of vitamin D supplementation; ***nmol/L divided by 2.5 = ng/mL 25-OH vitamin D. </p>
                </div>
            </div>
        </div>
    </div>
    <!-- Video -->
    <div class="video">
        <div class="video__thumbnail" (click)="openModal('screening-tool-landing-introduction-user-guide-how-to-use-video-button-cta')">
            <button class="circle-button circle-button--lg">
                <img src="assets/icons/icon-play.svg" alt="Play icon">
            </button>
        </div>
        <h4 class="video__caption">User guide: How to use the Screen for EPI in CP tool</h4>
        <div class="horizontal-block">
            <img src="assets/icons/icon-clock.svg" alt="Time icon">
            <p>3.5 minute watch</p>
        </div>
    </div>
    <div class="disclaimer">
        <img src="assets/icons/icon-info.svg" alt="Info icon">
        This tool suggests the likelihood of your patient having EPI. It is not a diagnostic tool for EPI and does not provide treatment recommendations for your patients.
    </div>
</div>
<!-- Ready to begin? Banner -->
<div class="banner banner--ready-to-begin">
    <div class="banner__info">
        <h3 class="banner__title">Ready to begin?</h3>
        <p class="banner__text">If you have viewed the <a (click)="openModal('screening-tool-landing-introduction-user-guide-how-to-use-ready-to-start-cta')">Screen for EPI in CP tool User Guide</a> and have all the information you need, click below to start using the Screen for EPI in CP tool</p>
        <app-button text="Start screening" path="screening-tool-landing" tab="start-screening" (btnClick)="track('screening-tool-landing-introduction-start-screening-bottom-cta')"></app-button>
    </div>
</div>
<!-- Nav Component -->
<app-bottom-navigation 
    backRoute="development"
    backTab="collaborators-publications" 
    backText="Collaborators & publications" 
    continueRoute="screening-tool-landing" 
    continueTab="start-screening" 
    continueText="Start screening">
</app-bottom-navigation>
<!-- Brightcove Video Modal -->
<app-modal *ngIf="isModalOpen" class="video-modal" (modalClose)="hideModal()">
    <app-video-player analyticsTitle="User guide: How to use the Screen for EPI in CP tool" [video]="{ brightcoveAccountId: '1029485116001', brightcovePlayerId: 'default', videoId: '6322975218112'}"></app-video-player>
</app-modal>
<!-- Table 2 Modal -->
<app-table2 *ngIf="tableIsOpen" (closeModalEvent)="closeTable()"></app-table2>